import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ApiError, AuthService, ConfigService, MessageService } from '../../../../core';
import { BaseTypeahead, BaseTypeaheadService } from '../../../../core/services/typeahead';
import { ConfigEmpresa, createNomeRepresentante } from '../../../configuracoes/app/empresa';
import { isTipoPessoaCentralCompras } from '../../tipo';
import { ClienteCadastroBadgePipe, ClienteCadastroPipe, clienteCadastroBaixado, clienteCadastroCliente, clienteCadastroOutros, clienteCadastroProspect, isClienteCadastroCliente } from '../cadastro';
import { Cliente } from '../cliente';
import { isClienteRevisaoStatusPendente } from '../cliente-revisao-status';
import { ClienteFilter } from '../filter/cliente-filter';
import { ClienteSituacaoBadgePipe, ClienteSituacaoPipe, clienteSituacaoAtivo, clienteSituacaoBaixado, clienteSituacaoBloqueado, clienteSituacaoCongelado, clienteSituacaoInativo, clienteSituacaoNovo, clienteSituacaoOutros, clienteSituacaoPositivo, clienteSituacaoPreBaixado, clienteSituacaoPreInativo, isClienteSituacaoAtivo } from '../situacao';
import { TypeaheadClienteHttpService } from './typeahead-cliente-http.service';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { ClienteCentralCompraParticipante } from '../central-compra/participante';
import { ClienteCentralCompraSelectParticipanteModalComponent } from '../central-compra/select-participante-modal';
import { ClienteFilterModalComponent } from '../filter/modal';
import { ClienteSearchesHttpService } from '../services';
import { ClienteTypeahead } from './cliente-typeahead';

@Component({
    selector: 'sgt-typeahead-cliente',
    templateUrl: './typeahead-cliente.component.html',
    styleUrls: ['./typeahead-cliente.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TypeaheadClienteComponent), multi: true },
        ClienteCadastroPipe,
        ClienteCadastroBadgePipe,
        ClienteSituacaoPipe,
        ClienteSituacaoBadgePipe
    ]
})
export class TypeaheadClienteComponent implements OnInit, AfterViewInit, BaseTypeahead<Cliente> {
    configEmpresa: ConfigEmpresa;
    isRepresentante: boolean;

    @Input() entity: Cliente;
    @Output() ngModelChange = new EventEmitter<Cliente>();

    /** Cadastro */
    @Input() somenteClientesEmCarteira: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente)
    @Input() somenteClientesEmCarteiraAtiva: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente) que estão com situação igual a Ativo
    @Input() somenteClientesEmCarteiraInativa: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente) que estão com situação igual a Inativo
    @Input() somenteClientes: boolean = null; // Somente os clientes com cadastro igual a cliente
    @Input() somenteClientesProspects: boolean = null; // Somente os clientes com cadastro prospect
    @Input() somenteClientesCongelados: boolean = null; // Somente os clientes com cadastro congelado
    @Input() somenteClientesBloqueados: boolean = null; // Somente os clientes com cadastro bloqueado
    @Input() somenteClientesBaixados: boolean = null; // Somente os clientes com cadastro baixado
    @Input() somenteClientesOutros: boolean = null; // Somente os clientes com cadastro outros
    /** Se deve exibir a mensagem quando o cliente possuir o seu cadastro incompleto */
    @Input() exibirCadastroIncompleto = false;
    /** Se deve obrigar a completar o cadastro incomplento do cliente para emitir o evento de edição do cliente */
    @Input() obrigarCompletarCadastroIncompleto = false;
    /** Mensagem informativa para completar cadastro do cliente */
    @Input() messageCompletarCadastro = 'É necessário complentar o cadastro deste cliente';
    @Input() cadastroProspect = false;
    @Input() cadastroCliente = false;
    @Input() cadastroBaixado = false;
    @Input() cadastroOutros = false;

    /** Situação */
    @Input() somenteClientesAtivos: boolean = null; // Somente os clientes com a situação ativo
    @Input() somenteClientesInativos: boolean = null; // Somente os clientes com a situação inativo
    @Input() situacaoNovo = false;
    @Input() situacaoPositivo = false;
    @Input() situacaoAtivo = false;
    @Input() situacaoPreInativo = false;
    @Input() situacaoInativo = false;
    @Input() situacaoCongelado = false;
    @Input() situacaoBloqueado = false;
    @Input() situacaoPreBaixado = false;
    @Input() situacaoBaixado = false;
    @Input() situacaoOutros = false;

    /** Revisão */
    @Input() exibirRevisaoPendente = false; // Se deve exibir a mensagem quando o cliente estiver com a revisão pendente

    /** Se é para considerar somente os clientes que são Centrais de Compras V1 */
    @Input() somenteCentralDeCompraV1 = false;
    /** Se é para considerar somente os clientes que são Centrais de Compras */
    @Input() somenteCentralDeCompra = false;
    /** Se é para considerar somente os clientes que estão disponíveis para serem participantes de uma central de compras */
    @Input() somenteParticipaveisCentalDeCompra = false;
    /** Serve para informar qual é o cliente atual em que o Typeahead está sendo utilizado para pesquisa */
    @Input() idClienteAtual: number = null;
    /** ID's dos clientes que devem ser ignorados e não devem retornar nos resultados do typeahead */
    @Input() idsIgnore: number[] = [];

    @Input() required: any;
    @Input() disabled: any;
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    @Input() autofocus = false;
    @Input() container = '';
    @Input() dropup = false;
    @Input() typeaheadOptionsInScrollableView = 5; // Quantidade de itens visíveis no no scroll do typeahead
    @Output() typeaheadChange = new EventEmitter<Cliente>();
    @Output() entitySelected = new EventEmitter<Cliente>(); // Emite evento quando a entidade foi selecionada no typeahead
    @Output() clearEntity = new EventEmitter<any>();

    @ViewChild('inputTypeahead', {static: true}) inputTypeahead: ElementRef;
    typeaheadOptionsLimit = 1000; // Quantidade máxima de registros exibidos no typeahead
    typeaheadSelected: string;
    typeaheadToken: string;
    typeaheadDataSource: Observable<Cliente[]>;
    typeaheadNoResults: boolean;

    currentRequest: Subscription;
    isLoading: boolean;

    @ViewChild('clienteFilterModal') clienteFilterModal: ClienteFilterModalComponent;

    // Central de Compras
    /** Se deve selecionar um participante quando for selecionado uma central de compras no typeahead */
    @Input() selectParticipanteCentralCompras = false;
    /** Subtitle do modal para selecionar o participante da central de compras */
    @Input() subTitleSelectParticipanteCentralComprasModal: string = null;
    /** Modal para selecionar um participante de uma central de compras */
    @ViewChild('clienteCentralCompraSelectParticipanteModal') clienteCentralCompraSelectParticipanteModal: ClienteCentralCompraSelectParticipanteModalComponent;

    // Insert/Edit cliente
    @Output() insertCliente = new EventEmitter<any>();
    @Output() editCliente = new EventEmitter<number>();

    propagateChange: any = () => {};

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private baseTypeaheadService: BaseTypeaheadService,
                private messageService: MessageService,
                private typeaheadClienteHttpService: TypeaheadClienteHttpService,
                private clienteSearchesHttpService: ClienteSearchesHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isRepresentante = this.authService.isRepresentante();

        this.createDataSource();
    }

    ngAfterViewInit() {
        if (this.autofocus) {
            jQuery(this.inputTypeahead.nativeElement).trigger('focus');
        }
    }

    /** Cria o filtro para pesquisar os clientes */
    private createFilter(): ClienteFilter {
        const filter = new ClienteFilter();
        // Nome / Código Integração / CNPJ/CPF
        filter.filterPsq = this.typeaheadToken;

        /** Cadastro */
        filter.somenteClientesEmCarteira = this.somenteClientesEmCarteira;
        filter.somenteClientesEmCarteiraAtiva = this.somenteClientesEmCarteiraAtiva;
        filter.somenteClientesEmCarteiraInativa = this.somenteClientesEmCarteiraInativa;
        filter.somenteClientes = this.somenteClientes;
        filter.somenteClientesProspects = this.somenteClientesProspects;
        filter.somenteClientesCongelados = this.somenteClientesCongelados;
        filter.somenteClientesBloqueados = this.somenteClientesBloqueados;
        filter.somenteClientesBaixados = this.somenteClientesBaixados;
        filter.somenteClientesOutros = this.somenteClientesOutros;
        // Prospect
        if (this.cadastroProspect) {
            filter.cadastros.push(clienteCadastroProspect.id);
        }
        // Cliente
        if (this.cadastroCliente) {
            filter.cadastros.push(clienteCadastroCliente.id);
        }
        // Baixado
        if (this.cadastroBaixado) {
            filter.cadastros.push(clienteCadastroBaixado.id);
        }
        // Outros
        if (this.cadastroOutros) {
            filter.cadastros.push(clienteCadastroOutros.id);
        }

        /** Situação */
        filter.somenteClientesAtivos = this.somenteClientesAtivos;
        filter.somenteClientesInativos = this.somenteClientesInativos;
        // Novo
        if (this.situacaoNovo) {
            filter.situacoes.push(clienteSituacaoNovo.id);
        }
        // Positivo
        if (this.situacaoPositivo) {
            filter.situacoes.push(clienteSituacaoPositivo.id);
        }
        // Ativo
        if (this.situacaoAtivo) {
            filter.situacoes.push(clienteSituacaoAtivo.id);
        }
        // PreInativo
        if (this.situacaoPreInativo) {
            filter.situacoes.push(clienteSituacaoPreInativo.id);
        }
        // Inativo
        if (this.situacaoInativo) {
            filter.situacoes.push(clienteSituacaoInativo.id);
        }
        // Congelado
        if (this.situacaoCongelado) {
            filter.situacoes.push(clienteSituacaoCongelado.id);
        }
        // Bloqueado
        if (this.situacaoBloqueado) {
            filter.situacoes.push(clienteSituacaoBloqueado.id);
        }
        // PreBaixado
        if (this.situacaoPreBaixado) {
            filter.situacoes.push(clienteSituacaoPreBaixado.id);
        }
        // Baixado
        if (this.situacaoBaixado) {
            filter.situacoes.push(clienteSituacaoBaixado.id);
        }
        // Outros
        if (this.situacaoOutros) {
            filter.situacoes.push(clienteSituacaoOutros.id);
        }

        // Somente central de compra V1
        filter.somenteCentralDeCompraV1 = this.somenteCentralDeCompraV1;
        // Somente central de compra
        filter.somenteCentralDeCompra = this.somenteCentralDeCompra;
        // Somente os clientes que estão disponíveis para serem participantes de uma central de compras
        filter.somenteParticipaveisCentalDeCompra = this.somenteParticipaveisCentalDeCompra;
        // Pessoal atual
        filter.idClienteAtual = this.idClienteAtual;
        // Clientes a serem ignorados
        filter.idsIgnore = this.idsIgnore;

        return filter;
    }

    /** Cria o data source do typeahead */
    createDataSource() {
        this.typeaheadDataSource = Observable.create((observer: any) => {
            if (this.currentRequest) {
                this.currentRequest.unsubscribe();
            }

            const filter = this.createFilter();
            this.currentRequest = this.typeaheadClienteHttpService.search(filter, this.typeaheadOptionsLimit)
                                        .subscribe(
                                            (entities: ClienteTypeahead[]) => observer.next(entities),
                                            (error: ApiError) => {
                                                this.messageService.error(error.message, 'Ocorreu um erro ao pesquisar os clientes. Tente novamente', error);
                                                observer.next([]);
                                            }
                                        );
        }).pipe(
          map((clientes: ClienteTypeahead[]) => {
              clientes.forEach(entity => {
                  entity.nomeRepresentante = createNomeRepresentante(this.configEmpresa.identificacaoRepresentante, entity.nomeRepresentante, entity.nomeFantasiaRepresentante);
              });

              return clientes;
          })
        );
    }


    /** Ao digitar no input do typeahead */
    onKeyup(e: KeyboardEvent) {
        this.baseTypeaheadService.onKeyup(this, e);
    }

    /** Ao sair do input do typeahead */
    onBlur() {
        this.baseTypeaheadService.onBlur(this);
        if (!this.entity) {
            this.clearEntity.emit();
        }
    }

    /** Verifica se o typeahead possui resultados */
    onChangeTypeaheadNoResults(noResults: boolean) {
        this.typeaheadNoResults = noResults;
    }

    /** Carrega o cliente informado para o Typeahead */
    private load(idCliente: number): Promise<ClienteTypeahead> {
        return new Promise<ClienteTypeahead>((resolve, reject) => {
            this.typeaheadClienteHttpService.load(idCliente)
                .pipe(
                    take(1)
                )
                .subscribe(
                    cliente => resolve(cliente),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o cliente', error);
                        reject();
                    }
                );
        });
    }


    /** Seta o cliente informado no typeahead */
    setEntity(idCliente: number) {
        this.load(idCliente)
            .then(cliente => {
                if (this.selectParticipanteCentralCompras && isTipoPessoaCentralCompras(cliente?.pessoa?.tipo)) { // Central compras
                    this.onSelectCentralCompras(cliente?.id);
                } else { // Cliente
                    this.selectEntity(cliente as any);
                }
            })
            .catch(() => {});
    }

    /** Seleciona o cliente no typeahead */
    private selectEntity(cliente: Cliente) {
        this.entity = cliente ?? null;

        this.propagateChange(this.entity);
        this.typeaheadChange.emit(this.entity);
        this.entitySelected.emit(this.entity);
        this.writeValue(this.entity);
        jQuery(this.inputTypeahead.nativeElement).trigger('focus');

        // Completar cadastro
        this.execCompletarCadastro();
    }

    /** Ao selecionar o cliente no typeahead */
    onSelectEntity(tm: TypeaheadMatch) {
        const cliente = tm.item as Cliente;
        if (this.selectParticipanteCentralCompras && isTipoPessoaCentralCompras(cliente?.pessoa?.tipo)) { // Selecionado central compras
            this.onSelectCentralCompras(cliente.id);
        } else { // Selecionado cliente
            this.baseTypeaheadService.onSelectEntity(this, tm);
            this.entitySelected.emit(this.entity);

            // Completar cadastro
            this.execCompletarCadastro();
        }
    }

    /** Ao selecionar o cliente no modal de pesquisa */
    onSelectEntityModal(cliente: Cliente) {
        if (this.selectParticipanteCentralCompras && isTipoPessoaCentralCompras(cliente?.pessoa?.tipo)) { // Selecionado central compras
            this.onSelectCentralCompras(cliente?.id);
        } else { // Selecionado cliente
            this.selectEntity(cliente);
        }
    }

    /** Abre o modal de pesquisa */
    search() {
        this.clienteFilterModal.open();
    }

    /** Limpa o typeahead e o model */
    clear() {
        this.baseTypeaheadService.clear(this);
        this.clearEntity.emit();
    }

    /** Seta o foco no input */
    setFocus() {
        jQuery(this.inputTypeahead.nativeElement).trigger('focus');
    }


   /** Quando recebe um objeto Cliente de "fora" do component */
    writeValue(value: Cliente) {
        if (value) {
            this.baseTypeaheadService.writeValue(this, value, value.pessoa.nome);

            // Obtendo a cidade e estado do cliente para exibir no template
            this.getCidadeEstadoCliente(value.id);
            // Obtem a rota de entrega do cliente no sistema ERP SCR
            this.getRotaEntregaErpSCR(value.id);
        } else {
            this.baseTypeaheadService.writeValue(this, null);
        }
    }

    /** Propaga as alterações */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {}

    /** Obtem o nome da cidade e estado do cliente */
    private getCidadeEstadoCliente(idCliente: number) {
        this.clienteSearchesHttpService.getCidadeEstado(idCliente)
            .pipe(
                take(1)
            )
            .subscribe(
                entity => {
                    if (entity) {
                        this.entity.nomeCidade = entity.nomeCidade;
                        this.entity.uf = entity.uf;
                    }
                },
                (() => {})
            );
    }

    /** Verifica se é um cliente com cadastro igual a Cliente */
    isClienteCadastroCliente(cadastro: number): boolean {
        return isClienteCadastroCliente(cadastro);
    }

    /** Obtem a rota de entrega do cliente no sistema ERP SCR */
    private getRotaEntregaErpSCR(idCliente: number) {
        this.clienteSearchesHttpService.getRotaEntregaErpSCR(idCliente)
            .pipe(
                take(1)
            )
            .subscribe(
                rotaEntregaErpSCR => this.entity.rotaEntregaErpSCR = rotaEntregaErpSCR,
                (() => {})
            );
    }

    /** Verifica se é um cliente com situação ativo */
    isClienteSituacaoAtivo(situacao: number): boolean {
        return isClienteSituacaoAtivo(situacao);
    }

    /** Verifica se deve exibir a mensagem quando o cliente possuir o seu cadastro incompleto */
    isExibirCardCadastroIncompleto(): boolean {
        return this.exibirCadastroIncompleto && (this.entity?.id && !this.entity?.cadastroCompleto);
    }

    /** Emite evento para editar cliente quando for obrigatório completar o cadastro incompleto do cliente */
    private execCompletarCadastro() {
        // Se for obrigatório e o cliente estiver com o cadastro incompleto
        if (this.obrigarCompletarCadastroIncompleto && !this.entity?.cadastroCompleto) {
            this.editCliente.emit(this.entity?.id);
        }
    }

    /** Emite evento para inserir um novo cliente */
    insertClienteClick() {
        this.insertCliente.emit();
    }

    /** Adiciona o cliente inserido ao typeahead */
    afterInsertCliente(cliente: Cliente) {
        this.entity = cliente;
    }

    /** Emite evento para editar o cliente selecionado no typeahead */
    editClienteClick() {
        this.editCliente.emit(this.entity?.id);
    }

    /** Atualiza o cliente editado no typeahead */
    afterEditCliente(cliente: Cliente) {
        this.entity = cliente;
    }

    /** Verifica se o status da revisão do cliente é Pendente */
    private isRevisaoPendente(): boolean {
        return isClienteRevisaoStatusPendente(this.entity?.revisaoStatus);
    }

    /** Se deve exibir o card com a mensagem de cliente com revisão pendente */
    isExibirCardRevisaoPendente(): boolean {
        return this.exibirRevisaoPendente && this.isRevisaoPendente();
    }

    /** Ao selecionar um cliente central de compras abrimos o modal para selecionar um participante desta central de compras */
    private onSelectCentralCompras(idClienteCentralCompras: number) {
        this.clienteCentralCompraSelectParticipanteModal?.open(idClienteCentralCompras);
        this.clear();
    }

    /** Ao selecionar um participante da central de compras setamos este no pedido */
    onSelectParticipanteCentralCompras(participante: ClienteCentralCompraParticipante) {
        this.load(participante.id)
            .then(cliente => {
                this.selectEntity(cliente as any);
            })
            .catch(() => {});
    }

    /** Ao cancelar a seleção de um participante da central de compras removemos o cliente/pessoa do pedido */
    onCancelSelectParticipanteCentralCompras() {
        this.clear();
    }
}
